import * as Collapsible from '@radix-ui/react-collapsible'
import {
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@radix-ui/react-dropdown-menu'
import type { ComponentProps } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { TIMELINE_COL_WIDTH_SECOND } from './timeline/constants.style'


import { styled, theme } from './stitches.config'
import { keyframes } from '@stitches/react'

export const ScientiaTitle = styled(Link, {
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

/** Custom NavLink component to handle styles for active links  */
export const NavLinkWithActiveStyles = (props: ComponentProps<typeof NavLink>) => (
  <NavLink
    {...props}
    style={({ isActive }) => ({
      backgroundColor: isActive ? theme.colors.elementActive.value : undefined,
      ...props.style,
    })}
  >
    {props.children}
  </NavLink>
)

export const ReactRouterShortcutLink = styled(NavLink, {
  position: 'relative',           // so your ::after underline can be absolutely positioned
  height: '2.75rem',
  borderRadius: '0.5rem',
  fontSize: 'medium',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2.75rem',
  color: '$sand11',
  transition: 'color 0.2s ease, background-color 0.2s ease',

  '& svg': {
    flexShrink: 0,
    width: '1.375rem',    // exactly 22px in rem
    height: '1.375rem',
    // fill: 'transparent',
    // stroke: 'black'
    // fill: 'currentColor', // pick up the link’s color
    // stroke: 'none',       // in case it’s an outline icon
  },
  /* hover: blue text on hover */
  '&:hover': {
    backgroundColor: '$blueHover',
    color: 'var(--colors-blue11)',
'& svg': {
      fill: 'var(--colors-blue11)',
      // stroke: 'var(--colors-blue11)',
    },

  },

  '&:.active': {
    fill: 'var(--colors-blue11)',

  },

  /* active: blue text + optional underline or bg-highlight */
  '&[aria-current="page"]': {
    // backgroundColor: '$blueClicked',
    backgroundColor: '$blueHover',
    color: 'var(--colors-blue11)',
    '& svg': {
      fill: 'var(--colors-blue11)',
    },


    /* if you want a little underline at the bottom: */
    '&::after': {
      content: "''",
      position: 'absolute',
      left: '0.2rem',
      right: '0.2rem',
      bottom: 0,
      height: '2px',
      background: 'var(--colors-blue11)',
      // borderRadius: '1px',
    },
  },
})


const itemStyle = {
  borderRadius: '0.5rem',
  height: '2.75rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '0.5rem',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '$neutral3',
    color: '$sand12',
  },
  variants: {
    link: {
      true: {
        color: '$sand11',
        backgroundColor: 'transparent',
        textUnderlineThickness: '2px',
        textUnderlineOffset: '4px',
        '&:hover': {
          backgroundColor: 'transparent',
          textDecoration: 'underline',
        },
        '&:focus-visible': {
          color: '$sand12',
          textDecoration: 'underline',
          outline: 'none',
        },
      },
    },
  },
}

export const DropdownIcon = styled('div', {
  padding: '0.75rem',
  svg: { display: 'block', size: 20 },
})

const indeterminateAnimation = keyframes({
  '0%': { transform: 'translateX(-100%)' },
  '100%': { transform: 'translateX(250%)' },
});

const cometAnimation = keyframes({
  '0%': { transform: 'translateX(-100%)' },
  '100%': { transform: 'translateX(400%)' }, // Moves a 25% wide bar across the screen
});



export const Header = styled('div', {
  height: '$navHeight',
  width: '100%',

  position: 'fixed',
  top: 0,
  left: 0,
  // zIndex: 10,
  zIndex: 30,
  display: 'flex',
  alignContent: 'center',

  padding: '0',
  backgroundColor: '$blueBackground',
  borderBottom: '0.0625rem solid $separator',

  // --- REGISTER PAGE OVERRIDE ---
  // These styles ONLY apply when the body has the 'register-page-active' class.
  '.register-page-active &': {
    // 1. Remove the default border to make way for the progress bar
    borderBottom: 'none',

    // 2. Add the background layer of the progress bar
    '&::before': {
      content: "''",
      position: 'absolute',
      left: 0,
      bottom: 0,
      height: '0.125rem',
      width: '100%',
      backgroundColor: '$separator',
    },

    // 3. Add the blue progress layer on top
    '&::after': {
      content: "''",
      position: 'absolute',
      left: 0,
      bottom: 0,
      height: '0.125rem',
      backgroundColor: '$blue9',
      width: 'var(--progress-width, 0%)',
      transition: 'width 0.4s ease-in-out',
    },
  },

  '.search-loading-active &': {
    '&::before': { // The grey track
      content: "''",
      position: 'absolute',
      left: 0,
      bottom: 0,
      height: '0.2rem',
      width: '100%',
      backgroundColor: '$separator',
    },
    '&::after': { // The moving "comet"
      content: "''",
      position: 'absolute',
      bottom: 0,
      left: 0,
      height: '0.2rem',
      width: '25%', // The comet is a shorter bar
      
      // The "disconnected tail" effect
      background: 'linear-gradient(to right, transparent, $blue9)',
      
      // The repeating animation
      animation: `${cometAnimation} 1.5s linear infinite`,
    },
  },
});

export const Nav = styled('nav', {
  maxWidth: '$appMainWidth',
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'space-between',
  padding: '0 1rem',
  margin: 'auto',
})

export const Logo = styled('img', {
  height: '2rem',
})

export const WebsiteTitle = styled('span', {
  margin: '0 0.5rem',
  fontSize: '$xl',
  fontWeight: 600,
})

export const Item = styled(DropdownMenuItem, itemStyle)

export const CheckboxItem = styled(DropdownMenuCheckboxItem, itemStyle)

export const Separator = styled(DropdownMenuSeparator, {
  height: '0.0625rem',
  margin: '0.25rem',
  backgroundColor: '$separator',
})

export const VerticalRule = styled('div', {
  borderLeft: '1px solid $neutral7',
  height: '100%',
  margin: '0 0.5rem',
})

export const YearSwitcherWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  gap: '0.5rem',
  fontSize: '$md',
  alignItems: 'center',
  verticalAlign: 'middle',
})

export const YearArrow = styled('button', {
  display: 'flex',
  width: 'fit-content',
  padding: '0.75rem',
  background: 'transparent',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '$sm',
    backgroundColor: '$neutral3',
  },
  variants: {
    disabled: {
      true: {
        visibility: 'hidden',
      },
    },
  },
})

export const ExpandedNavContainer = styled(Collapsible.Content, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'absolute',
  left: '0',
  top: '$navHeight',
  width: '100%',
  backgroundColor: '$appBackground',
  color: 'white',
  overflowY: 'scroll',
  maxHeight: `calc(100vh - $navHeight)`,
  // Add a dropshadow
  boxShadow: '0px 24px 56px -8px rgba(0, 0, 0, 0.25)',
})

const NAVBAR_ITEM_HEIGHT = '3.25rem'
const expandedNavItemStyle = {
  display: 'flex',
  height: NAVBAR_ITEM_HEIGHT,
  padding: '0rem 0.75rem',
  alignItems: 'center',
  gap: '0.625rem',
  alignSelf: 'stretch',

  // '& svg': {
  //   stroke: 'black !important',
  //   fill: 'transparent !important',
  //   transition: 'fill 0.2s ease-in-out',
  // },



  borderBottom: '1px solid',
  borderBottomColor: '$separator',

  '&:hover, &:active': {
    backgroundColor: '$neutral4',
    transition: 'background-color 0.3s ease-in-out',
'& span': {
      color: 'var(--colors-blue11)',
    },    '& svg': {
      fill: 'var(--colors-blue11)',
    },
  },

}
export const ReactExpandedNavItem = styled(NavLinkWithActiveStyles, expandedNavItemStyle)
export const ExternalExpandedNavItem = styled('a', expandedNavItemStyle)
export const ExpandedNavItem = styled('div', 
  
  expandedNavItemStyle,

)

export const IconTouchTargetExpander = styled('div', {
  width: NAVBAR_ITEM_HEIGHT,
  height: NAVBAR_ITEM_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '&:hover, &:active': {
    backgroundColor: '$elementHover',
    transition: 'background-color 0.3s ease-in-out',
  },
})

export const CollapsibleNavTrigger = styled(Collapsible.Trigger, {
  borderRadius: '0.5rem',
  '&:hover, &:active': {
    backgroundColor: '$elementHover',
    transition: 'background-color 0.3s ease-in-out',
  },
  "&[aria-expanded='true']": {
    backgroundColor: '$elementActive',
  },
})

export const ExpandedNavSectionHeader = styled('h4', {
  marginTop: '1.25rem',
  marginBottom: '1rem',
})
